import { isTopicNodeInherentlyLocked } from '@arnold/core';
import { clone } from 'ramda';
import { TFunction } from 'react-i18next';
import { ClassicPreset, GetSchemes } from 'rete';
import { HistoryAction } from 'rete-history-plugin';
import { Metrics, QuestionType, SelectedRange, TopicDetailQuery } from '../../generated/hooks';
import { COMMENT_TYPE } from './constants';
import { AddOptionControlData } from './controls/AddOptionControl';
import { CheckboxControlData } from './controls/CheckboxControl';
import { Comments } from './controls/Comments';
import { CommentsControlData } from './controls/CommentsControl';
import { DeleteCommentButton } from './controls/DeleteCommentButton';
import { DeleteCommentControl } from './controls/DeleteCommentControl';
import { MinMaxControlData } from './controls/MinMaxControl';
import { NumberWithTooltipControlData } from './controls/NumberWithTooltipControl';
import { OptionControlData } from './controls/OptionControl';
import { TextAreaControlData } from './controls/TextAreaControl';
import { StatementControlData } from './controls/StatementControl';
import { ValidationError } from './validation';

export type OutputKey = `opt-${number}`;

/**
 * Only select-like questions branch per choice (one output per option). Every other question
 * type routes through a single `opt-0` output.
 */
const hasPerChoiceOutputs = (type: NodeData['type']): boolean =>
  type === QuestionType.Select || type === QuestionType.Multiselect;

/**
 * Choice display order lives on the node (`optionsOrder` / control index), not in the
 * insertion order of `choices`. Rebuild the object in that order so a duplicate shows
 * the same sequence as the original.
 */
export const choicesInDisplayOrder = (choices: NodeData['choices'], optionsOrder: OutputKey[]): NodeData['choices'] => {
  const orderedKeys = optionsOrder.filter((key) => choices[key] != null);
  const remainingKeys = (Object.keys(choices) as OutputKey[]).filter((key) => !orderedKeys.includes(key));
  return Object.fromEntries(
    [...orderedKeys, ...remainingKeys].map((key) => [key, choices[key]]),
  ) as NodeData['choices'];
};

/**
 * Collapses `outputs` to a single `opt-0` for question types that must not branch per choice.
 */
export const normalizeSingleOutput = (type: NodeData['type'], outputs: NodeData['outputs']): NodeData['outputs'] => {
  const keys = Object.keys(outputs) as OutputKey[];
  if (hasPerChoiceOutputs(type) || keys.length <= 1) {
    return outputs;
  }
  const optionNumber = (key: OutputKey) => Number.parseInt(key.replace('opt-', ''), 10);
  const connectedKey = [...keys].sort((a, b) => optionNumber(a) - optionNumber(b)).find((key) => outputs[key] != null);
  return { 'opt-0': connectedKey ? outputs[connectedKey] : null };
};

export interface NodeData {
  questionIndex: number;
  questionText: string;
  isRequired: boolean;
  metrics: Metrics[];
  isQuestionTrigger: boolean;
  choices: { [key: OutputKey]: string };
  statements?: (string | undefined)[];
  statementIds?: string[];
  randomizeOrder?: boolean;
  showEscapeOption?: boolean;
  isRankingScale?: boolean;
  type: QuestionType | typeof COMMENT_TYPE;
  range?: SelectedRange;
  answersToHighlight?: { [key: OutputKey]: number };
  input: boolean;
  outputs: { [key: OutputKey]: number | null };
  position: (number | null)[];
  reportOrder: number | null;
  originalQuestionDefinition?: TopicDetailQuery['topicDetail']['allQuestions'][0];
  removeConnectionFromOutput?: (node: DataNode, outputName: string) => void | Promise<void>;
  onChange?: (node: DataNode) => void;
  onDelete?: () => DataNode;
  comments?: NonNullable<NonNullable<TopicDetailQuery['topicDetail']['commentGroups']>[0]['comments']>;
  topicId?: string;
  search?: string;
  commentGroupId?: string;
  onDataChange?: (data: NodeData) => void;
  addHistoryAction?: (action: HistoryAction) => void;
  onReportOrderChange?: () => void;
  /** Transient highlight after an AI patch — not persisted. */
  aiDiffStatus?: 'added' | 'modified';
  translationStatusOverride?: string;
  onMarkTranslationAsReviewed?: (questionDefinitionId: string, languageCode: string) => Promise<void>;
  onTranslationStatusChange?: (nodeId: string, status: string) => void;
  onAiTranslateNode?: (
    sourceText: string,
    sourceLang: string,
    targetLang: string,
    choices?: string[],
    statements?: string[],
  ) => Promise<{ questionText: string; choices: string[]; statements: string[] }>;
}

const sharedSocket = new ClassicPreset.Socket('socket');

function generateStatementId(): string {
  return `stmt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export class DataNode extends ClassicPreset.Node<
  { in?: ClassicPreset.Socket },
  { [key in string]: ClassicPreset.Socket },
  {
    [key: string]:
      | TextAreaControlData
      | OptionControlData
      | AddOptionControlData
      | CheckboxControlData
      | NumberWithTooltipControlData
      | ClassicPreset.Control
      | ClassicPreset.InputControl<'number'>
      | ClassicPreset.InputControl<'text'>
      | StatementControlData;
    questionText: TextAreaControlData;
    reportOrder: NumberWithTooltipControlData;
  }
> {
  width = 250; // needed by autoarrange plugin
  height?: number;
  nextOptionNumber: number;
  errors: ValidationError[] = [];
  lockEdit = false;
  showLockIcon = false;
  optionsOrder: OutputKey[] = []; // order of options in select/multiselect - used only for undo/redo actions
  choicesLabel?: string;
  statementsLabel?: string;
  topicEditorLockEditQuestionIndices: readonly number[] = [];
  topicEditorInputSocketLockedPair = false;

  constructor(
    public data: NodeData,
    public t: TFunction,
    public readOnly = false,
    public isSysAdmin = false,
    public languageCode: string,
  ) {
    super(getLocalizedLabel(data.type, t));
    if (data.outputs) {
      data.outputs = normalizeSingleOutput(data.type, data.outputs);
    }
    if (
      data.type !== QuestionType.Scale &&
      Object.keys(data.outputs).length > 1 &&
      Object.keys(data.outputs).length !== Object.keys(data.choices).length
    ) {
      throw new Error('Number of outputs and choices must be the same');
    }
    this.nextOptionNumber = Object.keys(data.choices).length ?? 0;
    // Normalize statements for Scale questions to be dense (no undefined holes)
    if (this.data.type === QuestionType.Scale) {
      this.data.statements = (this.data.statements ?? []).filter((s) => s != null);
      if (this.data.statements.length === 0) {
        this.data.statements = [''];
      }
      const ids = (this.data.statementIds ?? []).slice(0, this.data.statements.length);
      while (ids.length < this.data.statements.length) ids.push(generateStatementId());
      this.data.statementIds = ids;
      this.data.randomizeOrder = this.data.randomizeOrder ?? false;
      this.data.showEscapeOption = this.data.showEscapeOption ?? true;
      this.data.isRankingScale = this.data.isRankingScale ?? false;
      if (this.data.isRankingScale) {
        this.data.showEscapeOption = false;
      }
      this.syncRankingScaleChoices();
    }
    // Some special nodes like metric, sms, email node or question trigger for McD should be locked and not editable for normal users
    if (isTopicNodeInherentlyLocked(this.data)) {
      this.readOnly = true;
      this.lockEdit = true;

      if (this.isSysAdmin) {
        this.lockEdit = false;
        this.showLockIcon = true;
        this.readOnly = false;
      }
    }
    this.addInputsFromData();
    this.addOutputsFromData();
    this.addControlsFromData();
  }

  addInputsFromData() {
    if (this.data.input) {
      this.addInput('in', new ClassicPreset.Input(sharedSocket, undefined, true));
    }
  }

  addOutputsFromData() {
    Object.keys(this.data.outputs || {}).forEach((outputName) => {
      this.addOutput(outputName, new ClassicPreset.Output(sharedSocket, undefined, false));
    });
  }

  addControlsFromData() {
    this.addControl(
      'questionText',
      new TextAreaControlData(
        this.data.questionText || '',
        (newValue: string) => {
          const state = this.saveStateBeforeChanges();
          this.data.questionText = newValue;
          this.saveStateAfterChanges(state);
          this.onChange();
        },
        this.readOnly,
      ),
    );

    this.addControl(
      'reportOrder',
      new NumberWithTooltipControlData(
        this.data.reportOrder,
        this.t('reportOrderTooltip'),
        'reportOrderInput',
        (newValue: number | null) => {
          const state = this.saveStateBeforeChanges();
          this.data.reportOrder = newValue;
          this.saveStateAfterChanges(state);
          this.onChange();
          this.data.onReportOrderChange?.();
        },
        this.readOnly,
      ),
    );
    if (
      (this.data.type === QuestionType.Multiselect || this.data.type === QuestionType.PersonMultiselect) &&
      this.data.range
    ) {
      this.addControl(
        'mixMax',
        new MinMaxControlData(
          this.data.range,
          (range: SelectedRange) => {
            const state = this.saveStateBeforeChanges();
            this.data.range = range;
            this.saveStateAfterChanges(state);
            this.onChange();
          },
          this.readOnly,
        ),
      );
    }
    if (
      ([QuestionType.Select, QuestionType.Multiselect].includes(this.data.type as QuestionType) ||
        (this.data.type === QuestionType.Scale && this.isSysAdmin && !this.data.isRankingScale)) &&
      !this.readOnly
    ) {
      this.addControl(
        'addOption',
        new AddOptionControlData(
          () => {
            this.createNewOption();
          },
          false,
          'addOption',
        ),
      );
    }
    Object.keys(this.data.choices).forEach((controlKey, index) => {
      this.createOptionControl(controlKey as OutputKey, index);
    });

    if (this.data.type === QuestionType.Scale) {
      this.choicesLabel = this.t('scaleChoicesLabel');
      this.statementsLabel = this.t('scaleStatementsLabel');
      // Ensure dense array and at least one statement present
      this.data.statements = (this.data.statements ?? []).filter((s) => s != null);
      if (this.data.statements.length === 0) {
        this.data.statements = [''];
      }
      // Ensure statementIds are present and aligned with statements
      const currentLength = this.data.statements.length;
      const ids = (this.data.statementIds ?? []).slice(0, currentLength);
      while (ids.length < currentLength) ids.push(generateStatementId());
      this.data.statementIds = ids;
      if (!this.readOnly) {
        this.addControl(
          'addStatement',
          new AddOptionControlData(() => this.createNewStatement(), false, 'addStatement'),
        );
      }
      (this.data.statements || []).forEach((_val, index) => {
        this.createStatementControl(index);
      });
      this.addControl(
        'randomizeOrder',
        new CheckboxControlData(
          this.data.randomizeOrder ?? false,
          this.t('scaleRandomizeStatementsOrder'),
          (value) => {
            const state = this.saveStateBeforeChanges();
            this.data.randomizeOrder = value;
            this.saveStateAfterChanges(state);
            this.onChange();
          },
          this.readOnly,
          'checkbox-randomize-statements-order-ariel',
          'checkbox-randomize-statements-order-ariel',
        ),
      );
    }
    if (this.data.type === QuestionType.Freetext) {
      this.addControl(
        'requiredCheckbox',
        new CheckboxControlData(
          this.data.isRequired,
          this.t('answerRequired'),
          (value) => {
            const state = this.saveStateBeforeChanges();
            this.data.isRequired = value;
            this.saveStateAfterChanges(state);
          },
          this.readOnly,
        ),
      );
    }
    if (this.data.type === COMMENT_TYPE) {
      this.addControl(
        'comment',
        new CommentsControlData(this.data, Comments, (data) => this.onDataChange(data), this.readOnly),
      );
      this.addControl('deleteCommentButton', new DeleteCommentControl(this.data, DeleteCommentButton, this.readOnly));
    }
    this.updateOptionDeleteButtonsVisibility();
  }

  createNewOption() {
    if (this.data.isRankingScale) {
      return;
    }
    const state = this.saveStateBeforeChanges();
    const optionNumber = this.nextOptionNumber;
    this.nextOptionNumber = optionNumber + 1;
    const controlKey: OutputKey = `opt-${optionNumber}`;
    this.data.choices[controlKey] = '';
    this.createOptionControl(controlKey, optionNumber);
    if (this.data.type !== QuestionType.Scale) {
      this.data.outputs[controlKey] = null;
      this.addOutput(controlKey, new ClassicPreset.Output(sharedSocket, undefined, false));
    }
    this.updateOptionDeleteButtonsVisibility();
    this.saveStateAfterChanges(state);
    this.onChange();
  }

  createNewStatement() {
    const state = this.saveStateBeforeChanges();
    const stmts = (this.data.statements ?? []).filter((s) => s != null);
    stmts.push('');
    this.data.statements = stmts;
    const ids = (this.data.statementIds ?? []).slice(0, stmts.length - 1);
    ids.push(generateStatementId());
    this.data.statementIds = ids;
    this.syncRankingScaleChoices();
    const index = stmts.length - 1;
    this.createStatementControl(index);
    // Ensure delete button visible for all statements when more than 1
    Object.keys(this.controls)
      .filter((k) => k.startsWith('statement-'))
      .forEach((k) => {
        const ctrl = this.controls[k] as StatementControlData;
        ctrl.showDeleteButton = stmts.length > 1;
      });
    this.saveStateAfterChanges(state);
    if (this.data.isRankingScale) {
      this.requestUpdateOfControlsAndRerender();
    } else {
      this.onChange();
    }
  }

  createOptionControl(controlKey: OutputKey, index: number) {
    this.addControl(
      controlKey,
      new OptionControlData(
        this.data.choices[controlKey],
        index,
        controlKey,
        this.data.answersToHighlight?.[controlKey] ?? 0,
        (newValue: string) => {
          const state = this.saveStateBeforeChanges();
          this.data.choices[controlKey] = newValue;
          this.saveStateAfterChanges(state);
          this.onChange();
        },
        async (saveState = true) => {
          const state = saveState && this.saveStateBeforeChanges();
          delete this.data.choices[controlKey];
          if (this.data.type !== QuestionType.Scale) {
            delete this.data.outputs[controlKey];
            this.removeOutput(controlKey);
          }
          this.removeControl(controlKey);
          // Update remaining delete buttons
          this.updateOptionDeleteButtonsVisibility();
          if (state) this.saveStateAfterChanges(state);
          if (this.data.removeConnectionFromOutput) {
            await this.data.removeConnectionFromOutput(this, controlKey);
          }
          this.onChange();
        },
        (newChoiceSeverity) => {
          const state = this.saveStateBeforeChanges();
          const newAnswersToHighlight = { ...this.data.answersToHighlight };
          newAnswersToHighlight[controlKey] = newChoiceSeverity;
          this.data.answersToHighlight = newAnswersToHighlight;
          this.saveStateAfterChanges(state);
          this.onChange();
        },
        (from: number, to: number) => {
          if (this.data.isRankingScale) {
            return;
          }
          const state = this.saveStateBeforeChanges();
          // Get orderer control keys
          const controlKeys = Object.values(this.controls)
            .filter((c) => c instanceof OptionControlData)
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            .map((x) => (x as OptionControlData).key as OutputKey);

          // Switch the order in the array
          const dragged = controlKeys.splice(from, 1)[0];
          controlKeys.splice(to, 0, dragged);

          for (let i = 0; i < controlKeys.length; i++) {
            const key = controlKeys[i];
            this.controls[key].index = i;
          }
          // Update the order in optionsOrder - used for undo/redo actions
          this.optionsOrder = controlKeys;
          this.saveStateAfterChanges(state);
          this.onChange();
        },
        this.readOnly || !!this.data.isRankingScale,
        !this.data.isRankingScale, // show delete button will be updated later
        !this.readOnly,
      ),
    );
    if (!this.optionsOrder.includes(controlKey as OutputKey)) {
      this.optionsOrder.push(controlKey as OutputKey);
    }
  }

  createStatementControl(index: number) {
    this.addControl(
      `statement-${index}`,
      new StatementControlData(
        (this.data.statements || [])[index] ?? '',
        index,
        // use stable statement id for React key
        (this.data.statementIds || [])[index] ?? `${index}`,
        (newValue: string) => {
          const state = this.saveStateBeforeChanges();
          const stmts = (this.data.statements ?? []).filter((s) => s != null);
          stmts[index] = newValue;
          this.data.statements = stmts;
          this.saveStateAfterChanges(state);
          this.onChange();
        },
        (saveState = true) => {
          const state = saveState && this.saveStateBeforeChanges();
          const stmts = (this.data.statements ?? []).filter((s) => s != null);
          if (index >= 0 && index < stmts.length) {
            stmts.splice(index, 1);
          }
          this.data.statements = stmts;
          // maintain aligned ids
          const ids = (this.data.statementIds ?? []).slice(0, stmts.length + 1);
          if (index >= 0 && index < ids.length) {
            ids.splice(index, 1);
          }
          this.data.statementIds = ids;
          this.syncRankingScaleChoices();
          // reindex controls
          Object.keys(this.controls)
            .filter((k) => k.startsWith('statement-'))
            .forEach((k) => this.removeControl(k));
          stmts.forEach((_s, i) => this.createStatementControl(i));
          if (state) this.saveStateAfterChanges(state);
          if (this.data.isRankingScale) {
            this.requestUpdateOfControlsAndRerender();
          } else {
            this.onChange();
          }
        },
        (from: number, to: number) => {
          const state = this.saveStateBeforeChanges();
          const stmts = (this.data.statements ?? []).filter((s) => s != null);
          const dragged = stmts.splice(from, 1)[0];
          stmts.splice(to, 0, dragged);
          this.data.statements = stmts;
          // reorder ids accordingly
          const ids = (this.data.statementIds ?? []).slice(0, stmts.length + 1);
          const draggedId = ids.splice(from, 1)[0];
          ids.splice(to, 0, draggedId);
          this.data.statementIds = ids;
          // reindex controls
          Object.keys(this.controls)
            .filter((k) => k.startsWith('statement-'))
            .forEach((k) => this.removeControl(k));
          stmts.forEach((_s, i) => this.createStatementControl(i));
          this.saveStateAfterChanges(state);
          this.onChange();
        },
        this.readOnly,
        // Hide delete button if only one statement present
        (this.data.statements?.length ?? 0) > 1,
      ),
    );
  }

  syncRankingScaleChoices() {
    if (this.data.type !== QuestionType.Scale || !this.data.isRankingScale) {
      return;
    }

    const statements = this.data.statements ?? [];
    const choiceKeys = statements.map((_statement, index) => `opt-${index}` as OutputKey);
    const previousHighlights = this.data.answersToHighlight ?? {};

    this.data.choices = Object.fromEntries(
      choiceKeys.map((choiceKey, index) => [choiceKey, `${index + 1}.`]),
    ) as NodeData['choices'];
    this.data.answersToHighlight = Object.fromEntries(
      choiceKeys
        .filter((choiceKey) => previousHighlights[choiceKey] != null)
        .map((choiceKey) => [choiceKey, previousHighlights[choiceKey]]),
    ) as NodeData['answersToHighlight'];
    this.optionsOrder = choiceKeys;
    this.nextOptionNumber = choiceKeys.length;
  }

  onChange() {
    this.data.onChange && this.data.onChange(this);
  }
  onDataChange(data?: NodeData) {
    this.data.onDataChange && this.data.onDataChange(data ? data : this.data);
  }

  saveStateBeforeChanges() {
    const action = new CustomNodeAction(this);
    setTimeout(() => {
      // just checking if the devs did not forget about saving the state after changes
      if (action.newNodeData === undefined) {
        throw new Error(
          'saveStateAfterChanges was not called after saveStateBeforeChanges. You need to call both methods in a row to save history',
        );
      }
    }, 1000);
    return action;
  }

  saveStateAfterChanges(action: CustomNodeAction) {
    action.saveChanges(this);
    this.data.addHistoryAction && this.data.addHistoryAction(action);
  }

  /**
   * Update the controls directly with the NodeData - used in the undo/redo actions
   */
  updateControlsFromData() {
    this.label = getLocalizedLabel(this.data.type, this.t);

    this.controls.questionText.initialValue = this.data.questionText;
    this.controls.questionText.initialValueChangedIndicator = Date.now();
    this.controls.reportOrder.initialValue = this.data.reportOrder;
    this.controls.reportOrder.initialValueChangedIndicator = Date.now();
    if (this.controls.requiredCheckbox as CheckboxControlData) {
      (this.controls.requiredCheckbox as CheckboxControlData).initialValue = this.data.isRequired;
      (this.controls.requiredCheckbox as CheckboxControlData).initialValueChangedIndicator = Date.now();
    }
    const shouldHaveMinMax =
      (this.data.type === QuestionType.Multiselect || this.data.type === QuestionType.PersonMultiselect) &&
      !!this.data.range;
    if (shouldHaveMinMax) {
      if (!this.controls.mixMax) {
        this.addControl(
          'mixMax',
          new MinMaxControlData(
            this.data.range!,
            (range: SelectedRange) => {
              const state = this.saveStateBeforeChanges();
              this.data.range = range;
              this.saveStateAfterChanges(state);
              this.onChange();
            },
            this.readOnly,
          ),
        );
      } else {
        (this.controls.mixMax as MinMaxControlData).initialValue = this.data.range!;
      }
    } else if (this.controls.mixMax) {
      this.removeControl('mixMax');
    }
    const shouldHaveAddOption =
      ([QuestionType.Select, QuestionType.Multiselect].includes(this.data.type as QuestionType) ||
        (this.data.type === QuestionType.Scale && this.isSysAdmin && !this.data.isRankingScale)) &&
      !this.readOnly;
    if (shouldHaveAddOption && !this.controls.addOption) {
      this.addControl('addOption', new AddOptionControlData(() => this.createNewOption(), false, 'addOption'));
    } else if (!shouldHaveAddOption && this.controls.addOption) {
      this.removeControl('addOption');
    }
    this.syncRankingScaleChoices();
    Object.keys(this.data.choices).forEach((controlKey) => {
      let optionControl = this.controls[controlKey] as OptionControlData | undefined;
      if (!optionControl) {
        // Create new control
        this.createOptionControl(controlKey as OutputKey, this.optionsOrder.indexOf(controlKey as OutputKey));
        optionControl = this.controls[controlKey] as OptionControlData;
        if (this.data.type !== QuestionType.Scale) {
          this.addOutput(controlKey, new ClassicPreset.Output(sharedSocket, undefined, false));
        }
      }
      // Update existing controls
      optionControl.initialValue = this.data.choices[controlKey as OutputKey];
      optionControl.initialValueChangedIndicator = Date.now();
      optionControl.severity = this.data.answersToHighlight?.[controlKey as OutputKey] ?? 0;
      optionControl.index = this.optionsOrder.indexOf(controlKey as OutputKey);
      optionControl.readOnly = this.readOnly || !!this.data.isRankingScale;
      optionControl.canEditSeverity = !this.readOnly;
    });
    // Remove controls that are not in the data
    Object.entries(this.controls).forEach(([key, control]) => {
      if (key.startsWith('opt-') && this.data.choices[key as OutputKey] == null) {
        (control as OptionControlData).onDelete(false);
      }
    });
    // statements sync
    if (this.data.type === QuestionType.Scale) {
      this.data.randomizeOrder = this.data.randomizeOrder ?? false;
      this.data.showEscapeOption = this.data.showEscapeOption ?? true;
      this.data.isRankingScale = this.data.isRankingScale ?? false;
      if (this.data.isRankingScale) {
        this.data.showEscapeOption = false;
      }
      // Ensure dense statements before syncing controls
      this.data.statements = (this.data.statements ?? []).filter((s) => s != null);
      // Ensure ids alignment
      const currentLength = this.data.statements.length;
      const ids = (this.data.statementIds ?? []).slice(0, currentLength);
      while (ids.length < currentLength) ids.push(generateStatementId());
      this.data.statementIds = ids;
      (this.data.statements || []).forEach((_val, i) => {
        let statementControl = this.controls[`statement-${i}`] as StatementControlData | undefined;
        if (!statementControl) {
          this.createStatementControl(i);
          statementControl = this.controls[`statement-${i}`] as StatementControlData;
        }
        statementControl.initialValue = (this.data.statements || [])[i] ?? '';
        statementControl.initialValueChangedIndicator = Date.now();
        statementControl.index = i;
        statementControl.showDeleteButton = (this.data.statements?.length ?? 0) > 1;
      });
      this.updateOptionDeleteButtonsVisibility();
      // cleanup removed
      Object.keys(this.controls).forEach((key) => {
        if (key.startsWith('statement-')) {
          const idx = parseInt(key.replace('statement-', ''), 10);
          if ((this.data.statements || [])[idx] == null) {
            (this.controls[key] as StatementControlData).onDelete(false);
          }
        }
      });
      if (!this.controls.randomizeOrder) {
        this.addControl(
          'randomizeOrder',
          new CheckboxControlData(
            this.data.randomizeOrder,
            this.t('scaleRandomizeStatementsOrder'),
            (value) => {
              const state = this.saveStateBeforeChanges();
              this.data.randomizeOrder = value;
              this.saveStateAfterChanges(state);
              this.onChange();
            },
            this.readOnly,
            'checkbox-randomize-statements-order-ariel',
            'checkbox-randomize-statements-order-ariel',
          ),
        );
      } else {
        (this.controls.randomizeOrder as CheckboxControlData).initialValue = this.data.randomizeOrder;
        (this.controls.randomizeOrder as CheckboxControlData).initialValueChangedIndicator = Date.now();
      }
    }
    this.updateOptionDeleteButtonsVisibility();
  }

  private updateOptionDeleteButtonsVisibility() {
    const countChoices = Object.keys(this.data.choices).length;
    const showDeleteButton = countChoices > 2;
    Object.keys(this.data.choices).forEach((controlKey) => {
      const opt = this.controls[controlKey] as OptionControlData | undefined;
      if (opt) {
        if (this.data.type === QuestionType.Scale) {
          opt.showDeleteButton = !this.data.isRankingScale && this.isSysAdmin && showDeleteButton;
        } else {
          opt.showDeleteButton = showDeleteButton;
        }
      }
    });
  }

  updateRequested = false;
  /**
   * We are using this method to request an update of the controls and rerender the node only once to avoid multiple rerenders
   */
  requestUpdateOfControlsAndRerender() {
    if (this.updateRequested) return;
    this.updateRequested = true;
    setTimeout(() => {
      this.updateControlsFromData();
      this.onChange();
      this.updateRequested = false;
    });
  }
}

export interface DataNodeWithDimensions extends DataNode {
  // autoarrange plugin needs defined dimensions
  width: number;
  height: number;
}

export class CustomConnection<N extends DataNodeWithDimensions> extends ClassicPreset.Connection<N, N> {}

export type Schemes = GetSchemes<DataNodeWithDimensions, CustomConnection<DataNodeWithDimensions>>;

function getLocalizedLabel(type: QuestionType | typeof COMMENT_TYPE, t: TFunction): string {
  const translation = questionTypeToLocalization[type as keyof typeof questionTypeToLocalization] as string | undefined;
  return translation ? t(translation) : type;
}

const questionTypeToLocalization = {
  [QuestionType.Tell]: 'tellTitle',
  [QuestionType.Freetext]: 'freetextTitle',
  [QuestionType.Select]: 'selectTitle',
  [QuestionType.Multiselect]: 'multiselectTitle',
  [QuestionType.Scale]: 'scaleTitle',
  [QuestionType.PersonMultiselect]: 'personalMultiselectTitle',
  [QuestionType.Ending]: 'endingTitle',
  [COMMENT_TYPE]: 'commentTitle',
};

export class CustomNodeAction implements HistoryAction {
  persistentNode: DataNode;
  previousNodeData: NodeData;
  previousOptionsOrder: OutputKey[];
  newNodeData?: NodeData;
  newOptionsOrder?: OutputKey[];

  undo(): void | Promise<void> {
    this.persistentNode.data = clone(this.previousNodeData);
    this.persistentNode.optionsOrder = this.previousOptionsOrder;
    this.persistentNode.requestUpdateOfControlsAndRerender();
    document.getSelection()?.removeAllRanges(); // blur - needed workaround
  }

  redo(): void | Promise<void> {
    if (this.newNodeData === undefined || this.newOptionsOrder === undefined)
      throw new Error('redo called before saveChanges');
    this.persistentNode.data = clone(this.newNodeData);
    this.persistentNode.optionsOrder = this.newOptionsOrder;
    this.persistentNode.requestUpdateOfControlsAndRerender();
  }

  constructor(dataNodeBeforeChanges: DataNode) {
    this.persistentNode = dataNodeBeforeChanges;
    this.previousNodeData = clone(dataNodeBeforeChanges.data);
    this.previousOptionsOrder = clone(dataNodeBeforeChanges.optionsOrder);
  }

  saveChanges(dataNodeAfterChanges: DataNode) {
    this.newNodeData = clone(dataNodeAfterChanges.data);
    this.newOptionsOrder = clone(dataNodeAfterChanges.optionsOrder);
  }
}
